import React from 'react';
import { CmsTraveller } from '../../integrations/contentful/types';
import {
  baseXInPixels,
  baseXUnit,
  breakpoints,
  color,
  imageHoverOpacity,
  linkStyle,
  spacing
} from '../../styles/theme';
import EllipsisOverflow from '../common/EllipsisOverflow';
import H from '../elements/H';
import { t } from '../../utils/i18n';
import { Paths } from '../../urls/Paths';
import RouteLink from '../navigation/RouteLink';
import Paragraph from '../elements/Paragraph';
import Spacing from '../common/Spacing';
import CmsMedia from '../cms/CmsMedia';
import FlexDiv from '../common/FlexDiv';
import HyperLink from '../elements/HyperLink';

interface TravellerListPageCardProps {
  traveller: CmsTraveller;
  memoryCount: number;
}

const TravellerListPageCard: React.FunctionComponent<
  TravellerListPageCardProps
> = ({ traveller, memoryCount }) => {
  return (
    <div className="traveller-list-page-card" data-cy="traveller-card">
      <RouteLink
        path={Paths.Traveller}
        params={{
          slug: traveller.fields.slug
        }}
      >
        <HyperLink
          data-testid="traveller-link"
          data-cy="traveller-card"
          noLinkStateStyles
        >
          <FlexDiv>
            <div className="picture">
              {traveller.fields.profilePicture && (
                <CmsMedia
                  media={traveller.fields.profilePicture}
                  width={baseXInPixels(24)}
                  height={baseXInPixels(24)}
                  borderRadius={1}
                  crop
                />
              )}
            </div>
            <div className="content">
              <Spacing left={[0, 0, 1]} right={4} top={[0, 1]}>
                <H styling="h3">
                  <strong>
                    <span className="traveller-name">
                      {traveller.fields.title}
                    </span>
                  </strong>
                </H>
                <Paragraph>
                  <EllipsisOverflow>
                    {traveller.fields.shortInfo}
                  </EllipsisOverflow>
                </Paragraph>
                <Spacing top={1}>
                  {!!memoryCount && (
                    <FlexDiv>
                      <div className="memory-count">
                        <Paragraph data>{memoryCount}</Paragraph>
                      </div>
                      <Spacing left={1} top={[0.25, 0.75, 0.5]}>
                        <div data-testid="memory-count-text">
                          <Paragraph small>
                            {t('matka', 'matkaa', memoryCount !== 1)}
                          </Paragraph>
                        </div>
                      </Spacing>
                    </FlexDiv>
                  )}
                </Spacing>
              </Spacing>
            </div>
          </FlexDiv>
        </HyperLink>
      </RouteLink>
      {/*language=CSS*/}
      <style jsx>{`
        .traveller-list-page-card:hover :global(img) {
          opacity: ${imageHoverOpacity};
        }
        .traveller-list-page-card .traveller-name {
          ${linkStyle.withAnimation}
        }
        .traveller-list-page-card:hover .traveller-name {
          ${linkStyle.hover}
        }
        .traveller-list-page-card:active .traveller-name {
          ${linkStyle.active}
        }
        .picture {
          flex-shrink: 0;
          margin-right: ${spacing.one};
          width: ${baseXUnit(8)};
          height: ${baseXUnit(8)};
        }
        .content {
          flex-grow: 1;
          min-width: 0;
          align-self: baseline;
        }
        .memory-count {
          margin-top: -${spacing.fourth};
          color: ${color.purpleLight};
          background-color: ${color.lightBackground};
          border-radius: ${baseXUnit(0.5)};
          padding: 0 ${spacing.half};
          min-width: ${baseXUnit(2)};
          height: ${baseXUnit(2.2)};
        }
        @media ${breakpoints.medium} {
          .picture {
            width: ${baseXUnit(12)};
            height: ${baseXUnit(12)};
          }
          .memory-count {
            margin-top: 0;
            padding: ${spacing.half} ${spacing.one};
            min-width: ${baseXUnit(3)};
            height: ${baseXUnit(3)};
          }
        }
        @media ${breakpoints.large} {
          .picture {
            width: ${baseXUnit(12)};
            height: ${baseXUnit(12)};
          }
          .memory-count {
            margin-top: 0;
            padding: 0 ${spacing.one};
            min-width: ${baseXUnit(3)};
            height: ${baseXUnit(3)};
          }
          .memory-count-text {
            margin-top: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default TravellerListPageCard;
